import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { between, Device, from, until } from '@helpers/media';
import svgSwoosh from '@img/icons/background/mep-header-swoosh.svg?url';
import styled, { createGlobalStyle } from 'styled-components';
const GlobalStyles = createGlobalStyle `
  :root {
    // Set header height variables here
    --headerHeight: 125px;
    --stickyHeaderHeight: ${fluid(66, 110, Device.TabletLarge)};

    overflow-x: hidden;

    @media ${from(Device.TabletLarge)} {
      --headerHeight: 229px;
    }

    @media ${from(Device.DesktopSmall)} {
      --headerHeight: ${fluid(178, 196)};
    }
  }
`;
const Wrapper = styled.header `
  overflow-x: clip;
`;
const Container = styled.div `
  margin: 0 auto;
  max-width: var(--maxWidth);

  --maxWidth: 1920px;
  --navigationHeight: ${fluid(52, 64, Device.TabletLarge)};
  --mobilePhoneNumberHeight: 38px;

  &[data-site='ess'] {
    --basketOverlayBgColour: ${brand.global.red};
    --basketOverlayHighlightColor: ${brand.global.white};
    --ctaLinkColor: ${brand.global.white};
    --dropdownBorderColour: ${brand.global.white};
    --dropdownLinkBgActiveColour: ${brand.ess.secondary.darkGrey};
    --dropdownLinkFgActiveColour: ${brand.global.red};
    --featuredCardBgColour: ${brand.global.red};
    --featuredCardFgColour: ${brand.global.white};
    --featuredCardTagBgColour: ${brand.global.white};
    --featuredCardTagFgColour: ${brand.global.red};
    --logoBgColour: ${brand.global.white};
    --logoRingColour: ${brand.ess.secondary.darkestGrey};
    --navigationBgActiveColour: ${brand.ess.primary};
    --navigationBgColour: ${brand.global.white};
    --navigationFgActiveColour: ${brand.global.white};
    --navigationFgColour: ${brand.ess.secondary.darkGrey};
    --quickLinksBgColour: ${brand.ess.primary};
    --quickLinksFgColour: ${brand.global.white};
    --searchBorderColour: ${brand.global.white};
    --stickyNavBgColour: ${brand.ess.secondary.darkestGrey};
    --stickyNavFgColour: ${brand.global.white};
    --telephoneBgColour: ${brand.ess.primary};
    --telephoneFgColour: ${brand.global.white};
    --topBgColour: ${brand.ess.secondary.darkestGrey};
  }

  &[data-site='mep'] {
    --basketOverlayBgColour: ${brand.global.red};
    --basketOverlayHighlightColor: ${brand.global.white};
    --ctaLinkColor: ${brand.mep.primary};
    --dropdownBorderColour: ${brand.ess.secondary.darkGrey};
    --dropdownLinkBgActiveColour: ${brand.ess.primary};
    --featuredCardBgColour: transparent;
    --featuredCardFgColour: ${brand.global.white};
    --featuredCardTagBgColour: ${brand.global.red};
    --featuredCardTagFgColour: ${brand.global.white};
    --logoBgColour: ${brand.global.white};
    --logoRingColour: ${brand.mep.secondary.darkestGrey};
    --navigationBgActiveColour: ${brand.mep.secondary.darkestGrey};
    --navigationBgColour: ${brand.mep.secondary.lightestGrey};
    --navigationFgActiveColour: ${brand.global.white};
    --navigationFgColour: ${brand.mep.secondary.darkestGrey};
    --quickLinksBgColour: ${brand.mep.secondary.lightestGrey};
    --quickLinksFgColour: ${brand.mep.primary};
    --searchBorderColour: ${brand.mep.primary};
    --stickyNavBgColour: ${brand.mep.primary};
    --stickyNavFgColour: ${brand.global.white};
    --telephoneBgColour: ${brand.mep.primary};
    --telephoneFgColour: ${brand.global.white};
    --topBgColour: ${brand.global.white};
  }
`;
const TopWrapper = styled.div `
  display: flex;
  background-color: var(--topBgColour);

  @media ${from(Device.ActualDesktop)} {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 50dvw;
      height: 100%;
      background-color: var(--topBgColour);
      z-index: -1;
      transform: translateX(100%);
    }
  }
`;
const Logo = styled.a `
  display: flex;
  align-items: center;
  background-color: var(--logoBgColour);
  padding: ${fluid(8, 16)} ${fluid(8, 16)} ${fluid(8, 16)} ${fluid(16, 32)};

  ${Container}[data-site='ess'] & {
    clip-path: polygon(0 0, 100% 0, 90% 100%, 0% 100%);
    padding-right: ${fluid(32, 64)};
  }

  ${Container}[data-site='mep'] & {
    @media ${until(Device.MobileLarge)} {
      background-color: transparent;
    }
  }

  &.focus-ring {
    outline: auto;
    outline-offset: -3px;
    outline-width: 2px;
    outline-style: solid;
    outline-color: var(--logoRingColour);
  }
`;
const LogoImg = styled.div `
  ${Container}[data-site='ess'] & {
    width: ${fluid(102, 210)};
  }

  ${Container}[data-site='mep'] & {
    width: ${fluid(150, 260, Device.Mobile, Device.TabletLarge)};

    > svg {
      &:last-of-type {
        display: none;
      }
    }

    @media ${from(Device.DesktopLarge)} {
      width: 440px;

      > svg {
        &:first-of-type {
          display: none;
        }

        &:last-of-type {
          display: block;
        }
      }
    }
  }

  > svg {
    width: 100%;
    display: block;
  }
`;
const SearchForm = styled.form `
  width: 100%;
  position: relative;
  background-color: var(--topBgColour);

  @media ${from(Device.TabletLarge)} {
    display: none;
    width: ${fluid(360, 580, Device.TabletLarge)};
  }
`;
const TopContainer = styled.div `
  display: flex;
  padding: 0 ${fluid(22, 64)} 0 ${fluid(16, 144)};
  justify-content: space-between;
  align-items: center;
  gap: 6px 32px;
  flex-wrap: wrap;
  flex: 1;

  > ${SearchForm} {
    display: none;

    @media ${from(Device.TabletLarge)} {
      display: block;
      margin: 0 0 12px auto;
    }

    @media ${from(1492)} {
      margin-bottom: 0;
    }
  }

  @media ${from(Device.MobileLarge)} {
    ${Container}[data-site='mep'] & {
      background-image: url(${svgSwoosh});
      background-repeat: no-repeat;
    }
  }

  @media ${from(Device.TabletLarge)} {
    padding-bottom: ${fluid(12, 16)};

    ${Container}[data-site='mep'] & {
      padding-left: ${fluid(82, 96)};
    }
  }
`;
const QuickLinks = styled.ul `
  display: none;

  @media ${from(Device.TabletLarge)} {
    margin-left: auto;
    background-color: var(--quickLinksBgColour);
    display: flex;
    gap: 18px;
    padding: 6px 12px 6px 22px;
    height: 30px;

    ${Container}[data-site='ess'] & {
      clip-path: polygon(12px 0, 100% 0, 100% 100%, 0% 100%);
    }

    ${Container}[data-site='mep'] & {
      clip-path: polygon(0% 100%, 0% 92%, 1px 79%, 2px 71%, 3px 64%, 8px 42%, 20px 0%, 100% 0%, 100% 100%);
    }
  }
`;
const QuickLink = styled.a `
  ${transition('text-decoration-color, opacity')};
  ${fonts.daxWide.medium};

  color: var(--quickLinksFgColour);
  font-size: 16px;
  line-height: 18px;
  text-decoration: underline;
  text-decoration-color: transparent;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  opacity: 0.8;

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-width: 1px;
    outline-style: solid;
  }

  &.focus-ring,
  &:hover {
    text-decoration-color: var(--quickLinksFgColour);
    opacity: 1;
  }
`;
const SearchInner = styled.div `
  padding: 14px 16px;
`;
const SearchInput = styled.input `
  ${fonts.daxWide.regular};

  width: 100%;
  border: 0;
  border-bottom: 1px solid var(--searchBorderColour);
  color: var(--searchBorderColour);
  padding: 6px 44px 14px 12px;
  background-color: transparent;
  font-size: ${fluid(14, 18)};
  line-height: 1em;

  &::placeholder {
    color: var(--searchBorderColour);
    opacity: 0.8;
  }
`;
const SearchSubmit = styled.button `
  ${transition('opacity')};
  ${ButtonReset};

  position: absolute;
  top: 18px;
  right: 24px;
  display: block;
  width: ${fluid(20, 24)};
  height: ${fluid(20, 24)};
  color: var(--searchBorderColour);

  > svg {
    height: 100%;
    width: 100%;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-width: 2px;
    outline-style: solid;
  }

  &:hover {
    opacity: 0.66;
  }

  @media ${from(Device.TabletLarge)} {
    top: 4px;
    right: 8px;
  }
`;
const SearchError = styled.span `
  ${fonts.daxWide.regular};

  margin-top: 8px;
  font-size: ${fluid(12, 14)};
  line-height: ${fluid(16, 20)};
  color: ${brand.global.red};
`;
const FlexBreak = styled.span `
  display: none;

  @media ${from(Device.TabletLarge)} {
    display: block;
    flex-basis: 100%;
    height: 0;
  }
`;
const CtaLinks = styled.ul `
  display: flex;
  gap: ${fluid(16, 32)};
  margin-left: auto;
`;
const CtaLinkItem = styled.li `
  &:first-child {
    display: none;

    @media ${from(Device.MobileLarge)} {
      display: block;
    }
  }

  &:last-child {
    display: inline-flex;
    align-items: center;
    padding-left: 8px;

    @media ${from(Device.TabletLarge)} {
      display: none;
    }
  }
`;
const CtaLink = styled.a `
  ${ButtonReset};
  ${transition('opacity, text-decoration-color')};
  ${fonts.daxWide.medium};

  font-size: 0;
  color: var(--ctaLinkColor);
  text-decoration: underline;
  text-decoration-color: transparent;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  display: flex;
  flex-direction: column;
  align-items: center;

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-width: 1px;
    outline-style: solid;
  }

  &.focus-ring,
  &:hover {
    text-decoration-color: var(--ctaLinkColor);
    opacity: 0.8;
  }

  @media ${from(Device.TabletLarge)} {
    font-size: 16px;
    line-height: 22px;
    gap: 6px;
  }
`;
const CtaLinkIcon = styled.span `
  position: relative;
  display: block;
  width: ${fluid(30, 46, Device.TabletLarge)};

  > svg {
    display: block;
  }

  /* Used to shrink the cross icon the appropiate amount in comparison to the menu icon */
  &[data-active='true'] {
    padding: 0 14%;
  }
`;
const BasketCount = styled.span `
  ${fonts.daxWide.medium};

  position: absolute;
  top: 0;
  right: 0;
  background: var(--basketOverlayBgColour);
  font-size: ${fluid(10, 14)};
  line-height: 1em;
  border: 1px solid var(--basketOverlayHighlightColor);
  color: var(--basketOverlayHighlightColor);
  border-radius: 50%;
  display: grid;
  place-content: center;
  min-width: ${fluid(16, 18)};
  aspect-ratio: 1 / 1;
  padding: 0 2px;
  transform: translate(15%, -15%);
`;
const NavigationPlaceholder = styled.div `
  display: none;

  @media ${from(Device.TabletLarge)} {
    height: var(--navigationHeight);

    ${Container}[data-scroll-partial='true'] & {
      display: block;
    }
  }
`;
const PhoneLink = styled.a `
  ${fonts.daxWide.medium};

  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--telephoneFgColour);
  background-color: var(--telephoneBgColour);
  text-decoration: underline;
  text-decoration-color: transparent;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
  height: var(--mobilePhoneNumberHeight);

  > svg {
    display: block;
    margin: 0 10px 0 0;
    width: 22px;
  }

  @media ${between(Device.TabletLarge, Device.DesktopSmall)} {
    display: none;
  }

  @media ${from(Device.DesktopSmall)} {
    ${transition('text-decoration-color')};

    font-size: ${fluid(14, 16, Device.TabletLarge)};
    padding: ${fluid(14, 18)} ${fluid(22, 64)} ${fluid(14, 18)} ${fluid(32, 48, Device.TabletLarge)};
    margin: 0 calc(${fluid(22, 64)} * -1) 0 ${fluid(18, 22)};
    height: auto;

    ${Container}[data-site='ess'] & {
      clip-path: polygon(12px 0, 100% 0, 100% 100%, 0% 100%);
    }

    ${Container}[data-site='mep'] & {
      clip-path: polygon(0% 100%, 0% 92%, 1px 79%, 2px 71%, 4px 64%, 14px 42%, 36px 0%, 100% 0%, 100% 100%);
    }

    &.focus-ring {
      outline: auto;
      outline-offset: -3px;
      outline-width: 1px;
      outline-style: solid;
    }

    &.focus-ring,
    &:hover {
      text-decoration-color: var(--telephoneFgColour);
    }
  }
`;
const NavigationWrapper = styled.div `
  display: block;

  @media ${from(Device.TabletLarge)} {
    display: flex;
    justify-content: flex-end;
    padding: 0 ${fluid(22, 64)} 0 ${fluid(6, 16)};
    background-color: var(--navigationBgColour);
    position: relative;

    ${Container}[data-scroll-partial='true'] & {
      position: fixed;
      top: calc(var(--stickyHeaderHeight) + 16px);
      right: 0;
      transform: translateX(calc(100% + 4px));
      z-index: 999;
    }

    ${Container}[data-scroll-active='true'] & {
      ${transition('transform', 1.66)};

      box-shadow: 1px 0px 4px 1px rgba(0, 0, 0, 0.33);
      width: 100%;

      &[data-visible='false'] {
        pointer-events: none;
      }

      &[data-visible='true'] {
        transform: none;
        pointer-events: initial;
      }

      ${PhoneLink} {
        display: none;
      }
    }
  }

  @media ${from(Device.ActualDesktop)} {
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100dvw;
      height: 100%;
      background-color: var(--navigationBgColour);
      z-index: -2;
      transform: translateX(calc((var(--maxWidth) - 100dvw) / 2));
      pointer-events: none;
    }

    &:has(${PhoneLink}) {
      &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 50dvw;
        height: 100%;
        background-color: var(--telephoneBgColour);
        z-index: -1;
        transform: translateX(100%);
        pointer-events: none;
      }
    }

    ${Container}[data-scroll-partial='true'] & {
      &::before,
      &::after {
        opacity: 0;
      }
    }
  }
`;
const StickyWrapper = styled.div `
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  /* Required to ensure space doesn't interfere with content */
  pointer-events: none;
  z-index: 9;
`;
const StickyButtons = styled.div `
  ${transition('transform', 0.5, 'cubic-bezier(.43, 1.15, .84, 1.04)')};

  display: inline-flex;
  align-items: center;
  padding: ${fluid(18, 32, Device.TabletLarge)} ${fluid(16, 66, Device.TabletLarge)} ${fluid(18, 32, Device.TabletLarge)} 0;
  gap: ${fluid(20, 30, Device.TabletLarge)};
  background-color: var(--stickyNavBgColour);
  transform: translateX(133%);
  pointer-events: initial;

  ${Container}[data-scroll-active='true'] & {
    transform: none;
  }

  ${Container}[data-drawer-open='true'] & {
    transform: translateX(133%);
  }

  ${CtaLink} {
    color: var(--stickyNavFgColour);

    ${CtaLinkIcon} {
      &[data-active] {
        width: ${fluid(26, 30, Device.TabletLarge)};
      }
    }
  }

  [data-site='ess'] & {
    clip-path: polygon(20px 0, 100% 0, 100% 100%, 0% 100%);
    padding-left: ${fluid(32, 36, Device.TabletLarge)};
  }

  [data-site='mep'] & {
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      transform: translateX(-30%);
      display: block;
      background-color: inherit;
      z-index: -1;
      height: 100%;
      width: ${fluid(42, 56)};
      clip-path: ellipse(35% 50% at 35% 50%);
    }
  }
`;
const HeaderStyles = {
    BasketCount,
    Container,
    CtaLink,
    CtaLinkIcon,
    CtaLinkItem,
    CtaLinks,
    FlexBreak,
    GlobalStyles,
    Logo,
    LogoImg,
    NavigationPlaceholder,
    NavigationWrapper,
    PhoneLink,
    QuickLink,
    QuickLinks,
    SearchError,
    SearchForm,
    SearchInner,
    SearchInput,
    SearchSubmit,
    StickyButtons,
    StickyWrapper,
    TopContainer,
    TopWrapper,
    Wrapper,
};
export default HeaderStyles;
